<template>
    <app-layout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Manage Samples
            </h2>
        </template>

        <div>
            <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
                <div class="mb-4 text-sm shadow-sm sm:rounded-md flex items-stretch justify-start">
                    <div class="p-2 bg-glm-red text-red-100 flex items-center justify-center sm:rounded-l-md">
                        <font-awesome-icon
                            :icon="['fad', 'info']"
                            class="fa-fw"
                            size="lg"
                        ></font-awesome-icon>
                    </div>

                    <p class="p-2 bg-red-100 text-glm-red flex-1 sm:rounded-r-md">
                        Changes to the "value" field will destroy the graphs!<br><span class="font-bold">Please coordinate with the development team!</span>
                    </p>
                </div>

                <FormSection @submitted="createSample">
                    <template #title>
                        Create a new sample
                    </template>

                    <template #description>
                        Create a new sample to be used within the application.
                    </template>

                    <template #form>
                        <div class="col-span-6 sm:col-span-4">
                            <Label for="value" value="Value"/>
                            <Input id="value" type="text" class="mt-1 block w-full" v-model="createForm.value"
                                   ref="value"
                                   autocomplete="sample-value"/>
                            <InputError :message="createForm.errors.value" class="mt-2"/>
                            <HelpText>
                                Matches the first part of the filename e.g. <u>pooled</u>_marital_data.csv<br>
                                <span class="text-red-500">Exception is gender_* this edge case will be handled internally.</span>
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="label" value="Label"/>
                            <Input id="label" type="text" class="mt-1 block w-full" v-model="createForm.label"
                                   ref="value"
                                   autocomplete="label"/>
                            <InputError :message="createForm.errors.label" class="mt-2"/>
                            <HelpText>
                                Label which is shown to the user in the dropdown.
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="description" value="Description"/>
                            <Textarea rows="5" id="description" class="mt-1 block w-full"
                                      v-model="createForm.description"></Textarea>
                            <InputError :message="createForm.errors.description" class="mt-2"/>
                            <HelpText>
                                Brief description of the sample for the user.<br>
                                <span class="text-red-500">Currently not shown to the user!</span>
                            </HelpText>
                        </div>
                    </template>

                    <template #actions>
                        <ActionMessage :on="createForm.recentlySuccessful" class="mr-3">
                            Created.
                        </ActionMessage>

                        <ActionMessage :on="createForm.processing" class="mr-3">
                            Creating...
                        </ActionMessage>

                        <Button :class="{ 'opacity-25': createForm.processing }"
                                :disabled="createForm.processing">
                            Create
                        </Button>
                    </template>
                </FormSection>

                <template v-if="samples.length > 0">
                    <SectionBorder/>

                    <div class="flex items-center mb-6 mt-10 sm:mt-0 px-4 sm:px-0">
                        <h3 class="text-lg font-medium text-gray-900">Samples</h3>

                        <div class="ml-auto">
                            <jet-input
                                id="search"
                                type="text"
                                placeholder="Filter..."
                                class="block w-full"
                                v-model="search"
                            />
                        </div>
                    </div>

                    <table
                        class="w-full whitespace-nowrap sm:rounded-md bg-white divide-y divide-gray-300 overflow-hidden shadow">
                        <thead class="bg-gray-900">
                        <tr class="text-white">
                            <th class="font-semibold text-sm uppercase px-6 py-4 text-left w-64">value</th>
                            <th class="font-semibold text-sm uppercase px-6 py-4 text-left">label</th>
                            <th class="font-semibold text-sm uppercase px-6 py-4 text-right w-1">&nbsp;</th>
                        </tr>
                        </thead>

                        <tbody class="divide-y divide-gray-200">
                        <tr v-for="(sample, idx) in searchResults" :key="idx"
                            :class="{'bg-gray-100' : idx % 2 !== 0}">
                            <td class="px-6 py-4 w-64">
                                {{ sample.value }}
                            </td>

                            <td class="px-6 py-4">
                                {{ sample.label }}

                                <template v-if="sample.description !== ''">
                                    <br>
                                    <span class="text-gray-500">
                                        {{ sample.description }}
                                    </span>
                                </template>
                            </td>

                            <td class="px-6 py-4 text-left w-1">
                                <DangerButton @click.native="confirmDeletion(sample)" title="Delete" class="mr-2">
                                    <font-awesome-icon
                                        :icon="['fad', 'trash']"
                                    ></font-awesome-icon>
                                </DangerButton>

                                <SecondaryButton @click.native="editSample(sample)" title="Edit">
                                    <font-awesome-icon
                                        :icon="['fad', 'pencil']"
                                    ></font-awesome-icon>
                                </SecondaryButton>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </template>

                <DialogModal :show="confirmingDeletion" @close="closeDeleteModal">
                    <template #title>
                        Delete Sample
                    </template>

                    <template #content>
                        Are you sure you want to delete this sample?
                        After that, this sample can no longer be used within the application.
                    </template>

                    <template #footer>
                        <SecondaryButton @click.native="closeDeleteModal">
                            Nevermind
                        </SecondaryButton>

                        <DangerButton
                            class="ml-2"
                            @click.native="deleteSample"
                            :class="{ 'opacity-25': processing }"
                            :disabled="processing"
                        >
                            Delete Sample
                        </DangerButton>
                    </template>
                </DialogModal>

                <FormModal :show="editingModal" @submitted="submitEditForm">
                    <template #title>
                        Edit Sample
                    </template>

                    <template #content>
                        <div class="grid grid-cols-6 gap-6">
                            <div class="col-span-6">
                                <Label for="value" value="Value"/>
                                <Input id="value" type="text" class="mt-1 block w-full" v-model="form.value" ref="value"
                                       autocomplete="value"/>
                                <InputError :message="form.errors.value" class="mt-2"/>
                                <HelpText>
                                    Matches the first part of the filename e.g. <u>pooled</u>_marital_data.csv<br>
                                    <span class="text-red-500">Exception is gender_* this edge case will be handled internally.</span>
                                </HelpText>
                            </div>

                            <div class="col-span-6">
                                <Label for="label" value="Label"/>
                                <Input id="label" type="text" class="mt-1 block w-full" v-model="form.label" ref="value"
                                       autocomplete="label"/>
                                <InputError :message="form.errors.label" class="mt-2"/>
                                <HelpText>
                                    Label which is shown to the user in the dropdown.
                                </HelpText>
                            </div>

                            <div class="col-span-6">
                                <Label for="description" value="Description"/>
                                <Textarea rows="5" id="description" class="mt-1 block w-full"
                                          v-model="form.description"></Textarea>
                                <InputError :message="form.errors.description" class="mt-2"/>
                                <HelpText>
                                    Brief description of the sample for the user.<br>
                                    <span class="text-red-500">Currently not shown to the user!</span>
                                </HelpText>
                            </div>
                        </div>
                    </template>

                    <template #footer>
                        <SecondaryButton @click.native="closeEditModal">
                            Nevermind
                        </SecondaryButton>

                        <Button type="submit"
                                :class="{ 'opacity-25': form.processing }"
                                :disabled="form.processing"
                        >
                            Save
                        </Button>
                    </template>
                </FormModal>
            </div>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout';
import JetInput from '@/Jetstream/Input';
import DangerButton from "@/Jetstream/DangerButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import DialogModal from "@/Jetstream/DialogModal";
import FormModal from "@/Jetstream/FormModal";
import Button from "@/Jetstream/Button";
import Label from "@/Jetstream/Label";
import Input from "@/Jetstream/Input";
import InputError from "@/Jetstream/InputError";
import Textarea from "@/Jetstream/Textarea";
import SectionBorder from "@/Jetstream/SectionBorder";
import FormSection from "@/Jetstream/FormSection";
import ActionMessage from "@/Jetstream/ActionMessage";
import HelpText from "@/Jetstream/HelpText";

export default {
    components: {
        HelpText,
        ActionMessage,
        FormSection,
        SectionBorder,
        Textarea,
        InputError,
        Input,
        Label,
        Button,
        FormModal,
        DialogModal,
        SecondaryButton,
        DangerButton,
        AppLayout,
        JetInput,
    },

    props: ['samples'],

    data() {
        return {
            search: null,

            editingModal: false,
            confirmingDeletion: false,
            selectedSample: null,

            processing: false,

            form: this.$inertia.form({
                value: '',
                label: '',
                description: '',
            }),

            createForm: this.$inertia.form({
                value: '',
                label: '',
                description: '',
            }),
        };
    },

    computed: {
        searchResults() {
            if (this.search === null) {
                return this.samples;
            }

            return this.samples.filter(e => e.value.includes(this.search) || e.label.includes(this.search));
        },
    },

    methods: {
        createSample() {
            this.createForm.put(route('intern.sample.create'), {
                errorBag: 'createSample',
                preserveScroll: true,
                onSuccess: () => {
                    this.createForm.reset()
                },
            });
        },

        editSample(sample) {
            this.selectedSample = sample

            this.form.value = sample.value
            this.form.label = sample.label
            this.form.description = sample.description

            this.editingModal = true
        },

        submitEditForm() {
            this.form.post(route('intern.sample.update', this.selectedSample.id), {
                errorBag: 'updateSample',
                preserveScroll: true,
                onSuccess: () => {
                    this.closeEditModal()
                },
            })
        },

        confirmDeletion(sample) {
            this.selectedSample = sample;
            this.confirmingDeletion = true;
        },

        closeDeleteModal() {
            this.confirmingDeletion = false
            this.selectedSample = null
        },

        closeEditModal() {
            this.editingModal = false

            this.selectedSample = null
            this.form.reset()
        },

        deleteSample() {
            this.processing = true

            this.$inertia.delete(route('intern.sample.destroy', this.selectedSample.id), {
                onSuccess: () => {
                    this.processing = false

                    this.closeDeleteModal()
                },
                onError: () => {
                    this.processing = false
                }
            })
        }
    }
}
</script>
